import { Icon, Icons } from '@/components/icon';
import { getTagColors } from '@/components/ui/color-picker';
import { Badge, Dropdown } from '@/components/ui/design-system';
import type { CallTag } from '@/hooks/calls/use-ongoing-call-tags';
import { useTranslation } from '@/hooks/use-translation';
import { cn } from '@/lib/utils';
import { useEffect, useMemo, useRef, useState } from 'react';

const TRIGGER_BASE_CLASSES =
  'flex size-8 items-center justify-center rounded-xl border shadow-md transition-colors duration-100';

type CallTagsDropdownProps = {
  tags: CallTag[];
  selectedTagKeys: string[];
  onAddTag: (key: string) => void;
  onRemoveTag: (key: string) => void;
  container?: HTMLElement | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function CallTagsDropdown({
  tags,
  selectedTagKeys,
  onAddTag,
  onRemoveTag,
  container,
  open,
  onOpenChange,
}: CallTagsDropdownProps) {
  const { t } = useTranslation('desktop_ui');

  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  const hasNoTags = tags.length === 0;
  const selectedCount = selectedTagKeys.length;

  const filteredTags = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) {
      return tags;
    }
    return tags.filter((tag) => (tag.name ?? '').toLowerCase().includes(needle));
  }, [tags, search]);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => searchRef.current?.focus());
    } else {
      setSearch('');
    }
  }, [open]);

  if (hasNoTags) {
    return (
      <button
        type="button"
        disabled
        className={cn(
          TRIGGER_BASE_CLASSES,
          'border-primary-10 bg-white opacity-50 cursor-not-allowed',
        )}
      >
        <Icon name={Icons.Tag} className="text-body-18 text-primary" />
      </button>
    );
  }

  const isActive = open || selectedCount > 0;

  const trigger = (
    <span className="relative flex size-8 items-center justify-center">
      <Icon
        name={Icons.Tag}
        className={cn('text-body-18', isActive ? 'text-white' : 'text-primary')}
      />
      {selectedCount > 0 && !open && (
        <span className="absolute -top-1 -right-1 flex size-4 items-center justify-center rounded-full bg-highlight-default text-[10px] font-semibold text-white">
          {selectedCount}
        </span>
      )}
    </span>
  );

  const isInPip = container != null;

  return (
    <Dropdown
      showDuplicateHeader={false}
      open={open}
      onOpenChange={onOpenChange}
      align="center"
      width={isInPip ? 'calc(100vw - 16px)' : '240px'}
      sideOffset={4}
      collisionPadding={isInPip ? 8 : undefined}
      trigger={trigger}
      container={container}
      triggerClassName={cn(
        TRIGGER_BASE_CLASSES,
        'p-0 pr-0 cursor-pointer',
        isActive
          ? 'border-primary-70 bg-primary hover:bg-primary-90'
          : 'border-primary-10 bg-white hover:bg-primary-3',
      )}
      contentClassName={cn(
        'rounded-xl border border-primary-5 p-0 shadow-md h-[220px] flex flex-col',
        isInPip && 'max-w-[400px]',
      )}
    >
      <div
        className="flex flex-1 flex-col min-h-0"
        style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}
      >
        <div className="flex h-7 items-center pl-3 pr-4 pt-2">
          <span className="text-heading-12 text-primary-100">
            {t('components.callBar.callTags.header')}
          </span>
        </div>

        <div className="flex items-center gap-1 px-3 py-1.5">
          <Icon name={Icons.MagnifyingGlass} className="shrink-0 text-s text-primary-50" />
          <input
            ref={searchRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            placeholder={t('components.callBar.callTags.searchPlaceholder', 'Search tags')}
            className="min-w-0 flex-1 bg-transparent text-body-12 text-primary-100 outline-none placeholder:text-primary-40"
          />
        </div>
        <div className="h-px bg-primary-10" />

        <div className="flex flex-col p-2 overflow-y-auto">
          {filteredTags.length === 0 ? (
            <div className="flex h-7 items-center px-2">
              <span className="text-body-13 text-primary-50">
                {t('components.callBar.callTags.empty', 'No tags found')}
              </span>
            </div>
          ) : (
            filteredTags.map((tag) => {
              const tagKey = tag.key;
              if (!tagKey) {
                return null;
              }
              const isSelected = selectedTagKeys.includes(tagKey);
              return (
                <button
                  key={tagKey}
                  type="button"
                  onClick={() => (isSelected ? onRemoveTag(tagKey) : onAddTag(tagKey))}
                  className={cn(
                    'flex items-center gap-1 rounded-md p-1 outline-none transition-colors cursor-pointer justify-between',
                    isSelected ? 'bg-primary-3' : 'hover:bg-primary-3',
                  )}
                >
                  <Badge colors={getTagColors(tag.hex_color, tag.background_hex_color)}>
                    {tag.name}
                  </Badge>

                  {isSelected && (
                    <Icon
                      name={Icons.CircleCheck}
                      className="shrink-0 text-body-16 text-highlight-default"
                    />
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>
    </Dropdown>
  );
}
